import { Countries } from '@/comm'
import { readableCountryCodes } from '@/comm/product-tree'
import { useProductTreeStore } from '@/stores/product-tree-store'
import Selector from '@/components/zthd/Selector'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { ChevronDownIcon } from '@radix-ui/react-icons'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { Search } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

export const createCountryStore = (init = 'total', key = 'global') =>
  create(
    persist((set) => ({ value: init, update: (value) => set({ value }) }), {
      name: `country-storage-${key}`,
      storage: createJSONStorage(() => localStorage),
    }),
  )

export const useCountry = createCountryStore('total')

export const createCountryMultiStore = () =>
  create((set) => ({
    countries: [],
    updateSingle: (country) => set({ countries: [country] }),
    setAll: (countries) => set({ countries }),
    update: (country) =>
      set((state) => {
        const selectedCountries = new Set(state.countries)
        if (country === 'total') {
          selectedCountries.clear()
          selectedCountries.add('total')
        } else {
          selectedCountries.delete('total')
          if (selectedCountries.has(country)) {
            selectedCountries.delete(country)
          } else {
            selectedCountries.add(country)
          }
        }
        return { countries: Array.from(selectedCountries) }
      }),
  }))

export const isSelectedCountry = (country, countries = []) => {
  if (countries.includes('total')) {
    return true
  }
  return countries.includes(country)
}

/** 国家下拉选项：total + 当前用户在任一可读产品 / 分组上有权限的国家（含 '*' 时为全部） */
export const useCountryOptions = () => {
  const groups = useProductTreeStore((state) => state.groups)
  return useMemo(() => {
    const readable = readableCountryCodes(groups)
    if (!readable) return [...Countries]
    return Countries.filter((c) => c.code === 'total' || readable.has(c.code))
  }, [groups])
}

const DropdownCountry = () => {
  const value = useCountry((state) => state.value)
  const update = useCountry((state) => state.update)
  return <DropdownCountry1 value={value} update={update} />
}

export const DropdownCountry1 = ({ value, update }) => {
  const countries = useCountryOptions()
  return (
    <Selector
      datas={countries}
      showLabel
      labelName="Country"
      value={value}
      update={update}
      isSearchable={true}
    />
  )
}

export default DropdownCountry

export const useCountry2 = createCountryMultiStore()

export const DropdownCountryCombined = ({
  useSingleStore = useCountry,
  useMultiStore = useCountry2,
}) => {
  const [isMulti, setIsMulti] = useState(false)
  const [open, setOpen] = useState(false)
  const [searchText, setSearchText] = useState('')
  const searchInputRef = useRef(null)
  const [pendingCountries, setPendingCountries] = useState([])
  const countries = useCountryOptions()

  const singleValue = useSingleStore((state) => state.value)
  const updateSingle = useSingleStore((state) => state.update)

  const multiValues = useMultiStore((state) => state.countries)
  const setMultiAll = useMultiStore((state) => state.setAll)

  const effectiveIsMulti = isMulti || multiValues.length > 1

  // When switching to multi manually, seed pending from current single/multi store
  useEffect(() => {
    if (isMulti && multiValues.length <= 1) {
      setPendingCountries(
        multiValues.length > 0 ? multiValues : [singleValue],
      )
    }
  }, [isMulti, multiValues, singleValue])

  // External multi-select (e.g. URL params) → enable multi UI
  useEffect(() => {
    if (multiValues.length > 1) {
      setIsMulti(true)
      setPendingCountries(multiValues)
    }
  }, [multiValues])

  const filteredCountries = useMemo(() => {
    const text = searchText.trim().toLowerCase()
    if (!text) return countries
    return countries.filter((c) =>
      `${c.code} ${c.name}`.toLowerCase().includes(text),
    )
  }, [searchText, countries])

  const handleSearchChange = useCallback((e) => {
    setSearchText(e.target.value)
  }, [])

  const handleSearchPointerDown = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleSearchClick = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    if (searchInputRef.current) {
      searchInputRef.current.focus()
    }
  }, [])

  const handleSearchKeyDown = useCallback((e) => {
    e.stopPropagation()
    if (e.key === 'Escape' && searchInputRef.current) {
      searchInputRef.current.blur()
    }
  }, [])

  const formatSelectedCountries = (list) => {
    if (!list || list.length === 0) return 'Countries'
    if (list.includes('total')) return 'total'
    if (list.length > 5) {
      const first = list.slice(0, 4).join(',')
      const others = list.length - 4
      return `${first},Other(${others})`
    }
    return list.join(',')
  }

  const selectedText = effectiveIsMulti
    ? formatSelectedCountries(multiValues)
    : `${singleValue}`

  const allCodes = useMemo(() => countries.map((c) => c.code), [countries])
  const allExceptJP = useMemo(
    () => allCodes.filter((code) => code !== 'JP' && code !== 'total'),
    [allCodes],
  )
  const allExceptJPKR = useMemo(
    () => allCodes.filter((code) => !['JP', 'KR', 'total'].includes(code)),
    [allCodes],
  )
  const allExceptBRMXJPKR = useMemo(
    () =>
      allCodes.filter((code) => !['BR', 'MX', 'JP', 'KR', 'total'].includes(code)),
    [allCodes],
  )
  const allExceptBRMXUS = useMemo(
    () =>
      allCodes.filter((code) => !['BR', 'MX', 'US', 'total'].includes(code)),
    [allCodes],
  )
  const withoutJPChecked = useMemo(() => {
    const set = new Set(pendingCountries)
    if (set.has('JP') || set.has('total')) return false
    if (set.size !== allExceptJP.length) return false
    return allExceptJP.every((code) => set.has(code))
  }, [allExceptJP, pendingCountries])
  const withoutJPKRChecked = useMemo(() => {
    const set = new Set(pendingCountries)
    if (set.has('JP') || set.has('KR') || set.has('total')) return false
    if (set.size !== allExceptJPKR.length) return false
    return allExceptJPKR.every((code) => set.has(code))
  }, [allExceptJPKR, pendingCountries])
  const withoutBRMXJPKRChecked = useMemo(() => {
    const set = new Set(pendingCountries)
    if (set.has('BR') || set.has('MX') || set.has('JP') || set.has('total'))
      return false
    if (set.size !== allExceptBRMXJPKR.length) return false
    return allExceptBRMXJPKR.every((code) => set.has(code))
  }, [allExceptBRMXJPKR, pendingCountries])
  const withoutBRMXUSChecked = useMemo(() => {
    const set = new Set(pendingCountries)
    if (set.has('BR') || set.has('MX') || set.has('US') || set.has('total'))
      return false
    if (set.size !== allExceptBRMXUS.length) return false
    return allExceptBRMXUS.every((code) => set.has(code))
  }, [allExceptBRMXUS, pendingCountries])

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline">
          {selectedText} <ChevronDownIcon className="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-80 overflow-y-auto p-0">
        <div
          className="flex items-center border-b px-3 pb-2"
          onPointerDown={handleSearchPointerDown}
          onPointerMove={handleSearchPointerDown}
          onClick={handleSearchClick}
        >
          <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
          <Input
            ref={searchInputRef}
            placeholder="Search countries..."
            value={searchText}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onPointerDown={handleSearchPointerDown}
            onClick={handleSearchClick}
            className="h-8 w-full border-0 bg-transparent p-0 text-sm outline-none focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
        </div>
        <div className="max-h-72 overflow-y-auto p-2">
          <DropdownMenuLabel>
            {effectiveIsMulti ? 'Select Countries' : 'Select Country'}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {effectiveIsMulti && (
            <>
              <DropdownMenuCheckboxItem
                key="without-jp"
                className="capitalize"
                checked={withoutJPChecked}
                onCheckedChange={(checked) => {
                  setPendingCountries((prev) => {
                    if (checked) {
                      return allExceptJP
                    }
                    const set = new Set(prev)
                    set.delete('JP')
                    set.delete('total')
                    return Array.from(set)
                  })
                }}
                onSelect={(e) => e.preventDefault()}
              >
                without JP
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                key="without-jp-kr"
                className="capitalize"
                checked={withoutJPKRChecked}
                onCheckedChange={(checked) => {
                  setPendingCountries((prev) => {
                    if (checked) {
                      return allExceptJPKR
                    }
                    const set = new Set(prev)
                    set.delete('JP')
                    set.delete('KR')
                    set.delete('total')
                    return Array.from(set)
                  })
                }}
                onSelect={(e) => e.preventDefault()}
              >
                without JP/KR
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                key="without-br-mx-jp-kr"
                className="capitalize"
                checked={withoutBRMXJPKRChecked}
                onCheckedChange={(checked) => {
                  setPendingCountries((prev) => {
                    if (checked) {
                      return allExceptBRMXJPKR
                    }
                    const set = new Set(prev)
                    set.delete('BR')
                    set.delete('MX')
                    set.delete('JP')
                    set.delete('KR')
                    set.delete('total')
                    return Array.from(set)
                  })
                }}
                onSelect={(e) => e.preventDefault()}
              >
                without BR/MX/JP/KR
              </DropdownMenuCheckboxItem>
              <DropdownMenuCheckboxItem
                key="without-br-mx-us"
                className="capitalize"
                checked={withoutBRMXUSChecked}
                onCheckedChange={(checked) => {
                  setPendingCountries((prev) => {
                    if (checked) {
                      return allExceptBRMXUS
                    }
                    const set = new Set(prev)
                    set.delete('BR')
                    set.delete('MX')
                    set.delete('US')
                    set.delete('total')
                    return Array.from(set)
                  })
                }}
                onSelect={(e) => e.preventDefault()}
              >
                without BR/MX/US
              </DropdownMenuCheckboxItem>
            </>
          )}
          {effectiveIsMulti
            ? filteredCountries.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.code}
                className="capitalize"
                checked={isSelectedCountry(c.code, pendingCountries)}
                onCheckedChange={() => {
                  setPendingCountries((prev) => {
                    // Special interactions when "without BR/MX/JP/KR" is active
                    if (withoutBRMXJPKRChecked) {
                      if (['BR', 'MX', 'JP', 'KR'].includes(c.code)) {
                        // Clicking BR/MX/JP/KR switches to total
                        return ['total']
                      }
                      // Clicking any other country selects only that country
                      return [c.code]
                    }

                    // Special interactions when "without JP/KR" is active
                    if (withoutJPKRChecked) {
                      if (['JP', 'KR'].includes(c.code)) {
                        // Clicking JP/KR switches to total
                        return ['total']
                      }
                      // Clicking any other country selects only that country
                      return [c.code]
                    }

                    // Special interactions when "without JP" is active
                    if (withoutJPChecked) {
                      if (c.code === 'JP') {
                        // Clicking JP switches to total
                        return ['total']
                      }
                      // Clicking any non-JP selects only that country
                      return [c.code]
                    }

                    // Special interactions when "without BR/MX/US" is active
                    if (withoutBRMXUSChecked) {
                      if (['BR', 'MX', 'US'].includes(c.code)) {
                        // Clicking BR/MX/US switches to total
                        return ['total']
                      }
                      // Clicking any other country selects only that country
                      return [c.code]
                    }

                    // Default multi-select toggle behavior
                    const set = new Set(prev)
                    if (c.code === 'total') {
                      return ['total']
                    }
                    set.delete('total')
                    if (set.has(c.code)) {
                      set.delete(c.code)
                    } else {
                      set.add(c.code)
                    }
                    return Array.from(set)
                  })
                }}
                onSelect={(e) => e.preventDefault()}
              >
                {c.name}
              </DropdownMenuCheckboxItem>
            ))
            : filteredCountries.map((c) => (
              <DropdownMenuCheckboxItem
                key={c.code}
                className="capitalize"
                checked={singleValue === c.code}
                onCheckedChange={() => {
                  updateSingle(c.code)
                  setMultiAll([])
                }}
              >
                {c.name}
              </DropdownMenuCheckboxItem>
            ))}
        </div>
        <div className="sticky bottom-0 w-full bg-background">
          <DropdownMenuSeparator />
          <div className="flex items-center justify-between px-3 py-2">
            <div className="flex items-center gap-2">
              <label className="text-sm flex items-center gap-2">
                <Checkbox
                  checked={isMulti}
                  onCheckedChange={(checked) => {
                    const next = checked === true
                    setIsMulti(next)
                    if (!next) {
                      setMultiAll([])
                      setPendingCountries([])
                    }
                  }}
                />
                Multi Select
              </label>
            </div>
            {effectiveIsMulti && (
              <Button
                size="sm"
                variant="secondary"
                onClick={() => {
                  setMultiAll(
                    pendingCountries.length > 0 ? pendingCountries : ['total'],
                  )
                  setOpen(false)
                }}
              >
                Confirm
              </Button>
            )}
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
